import { useEffect, useRef } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useQueryClient } from "@tanstack/react-query";
import { useProfile, useUpdateProfile } from "@/hooks/use-profile";

/**
 * #2165: the server's "today" follows the account's IANA zone, so Plan's header, the pay
 * period and "due today" say the same date as Home (which reads this browser's clock).
 * The server stored no zone and used the UTC date: tomorrow, every evening, for a US user.
 */

/** This browser's IANA zone ("America/Denver"), or null when the browser can't say. */
export function deviceTimeZone(): string | null {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return typeof zone === "string" && zone.length > 0 ? zone : null;
  } catch {
    return null;
  }
}

/** The zone to write up: this device's, when the account has none or a different one. */
export function timeZoneToSend(
  server: string | null | undefined,
  device: string | null,
): string | null {
  if (!device) return null;
  return server === device ? null : device;
}

/**
 * Mounted once at the app root beside AccountThemeSync, active only when signed in. On each
 * account's first profile load (a sign-in to another account without a reload counts as a
 * new one) it sends this browser's zone when the account's differs, so the zone is
 * refreshed every session: a trip, or a second device in another zone, moves it. Once the
 * server has the new zone every query is refetched, because each date the server sent was
 * worked out on the old one.
 */
export function AccountTimezoneSync() {
  const { isSignedIn } = useAuth();
  const { data: me } = useProfile({ enabled: isSignedIn === true });
  const updateProfile = useUpdateProfile();
  const queryClient = useQueryClient();
  const settledFor = useRef<number | null>(null);

  useEffect(() => {
    if (!isSignedIn || !me || settledFor.current === me.id) return;
    settledFor.current = me.id;
    const zone = timeZoneToSend(me.profile?.timezone, deviceTimeZone());
    if (zone === null) return;
    updateProfile.mutate(
      { profile: { timezone: zone } },
      { onSuccess: () => void queryClient.invalidateQueries() },
    );
    // Once per account by design, like AccountThemeSync.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, me]);

  return null;
}
